import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BASE_URL } from '../../insightConfig/insightConfig';
import { Table, Container, Card, Badge, Modal, Button, Form } from 'react-bootstrap';
import { FaEdit } from 'react-icons/fa';

const ExamBodyInfo = () => {
    const [examBodies, setExamBodies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [editingBody, setEditingBody] = useState({
        sn: '',
        examBody: '',
        examBodyValue: '',
        examDate: '',
        examStatus: 'Active',
        examBodyUrl: ''
    });

    const fetchExamBodies = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${BASE_URL}/getExamBodiesNew`);
            if (response.data && response.data.examBodies) {

                setExamBodies(response.data.examBodies);
            }
            setLoading(false);
        } catch (err) {
            console.error("Error fetching exam bodies:", err);
            setError(err.message);
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchExamBodies();
    }, []);

    const handleUpdateClick = (body) => {
        setEditingBody({
            ...body,
            examDate: body.examDate ? body.examDate.split('T')[0] : ''
        });
        setShowModal(true);
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setEditingBody(prev => ({ ...prev, [name]: value }));
    };

    const handleUpdateSubmit = async () => {
        try {
            const response = await axios.post(`${BASE_URL}/updateExamBodyNew`, editingBody);
            if (response.status === 200) {

                setShowModal(false);
                fetchExamBodies();
                // Using a simple alert for feedback as per common patterns in this repo
                alert("Exam Body updated successfully!");
            }
        } catch (err) {
            console.error("Error updating exam body:", err);
            alert("Failed to update Exam Body: " + err.message);
        }
    };

    if (loading && examBodies.length === 0) return <Container className="mt-5 text-center"><h5>Loading Exam Bodies...</h5></Container>;
    if (error) return <Container className="mt-5 text-center text-danger"><h5>Error: {error}</h5></Container>;

    return (
        <Container className="my-5">
            <Card className="shadow-sm border-0 rounded-4 overflow-hidden">
                <Card.Header className="bg-dark text-white p-4">
                    <div className="d-flex justify-content-between align-items-center">
                        <h4 className="mb-0 fw-bold">Exam Body Information</h4>
                        <Badge bg="primary" className="px-3 py-2">{examBodies.length} Records</Badge>
                    </div>
                </Card.Header>
                <Card.Body className="p-0">
                    <div className="table-responsive">
                        <Table hover striped className="mb-0 align-middle">
                            <thead className="bg-light border-bottom border-2">
                                <tr>
                                    <th className="px-4 py-3">SN</th>
                                    <th className="py-3">Exam Body</th>
                                    <th className="py-3">Value</th>
                                    <th className="py-3">Exam Date</th>
                                    <th className="py-3">Status</th>
                                    <th className="px-4 py-3">URL</th>
                                    <th className="px-4 py-3 text-end">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {examBodies.length > 0 ? (
                                    examBodies.map((body, index) => (
                                        <tr key={body.sn || index}>
                                            <td className="px-4 fw-bold">{body.sn}</td>
                                            <td className="fw-semibold text-primary">{body.examBody}</td>
                                            <td className="text-muted small">{body.examBodyValue || '-'}</td>
                                            <td className="fw-bold">{body.examDate ? new Date(body.examDate).toLocaleDateString() : '-'}</td>
                                            <td>
                                                <Badge bg={body.examStatus === "Active" ? "success" : "secondary"}>
                                                    {body.examStatus !== undefined && body.examStatus !== null ? body.examStatus : 'N/A'}
                                                </Badge>
                                            </td>

                                            <td className="px-4 text-muted small">
                                                {body.examBodyUrl || '-'}
                                            </td>
                                            <td className="px-4 text-end">
                                                <Button 
                                                    variant="outline-primary" 
                                                    size="sm" 
                                                    className="rounded-pill d-inline-flex align-items-center gap-2"
                                                    onClick={() => handleUpdateClick(body)}
                                                >
                                                    <FaEdit /> Update
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="7" className="text-center py-5 text-muted">No Exam Bodies Found</td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                </Card.Body>
            </Card>

            {/* Update Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
                <Modal.Header closeButton className="bg-primary text-white">
                    <Modal.Title className="fw-bold">Update Exam Body [ID: {editingBody.sn}]</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4">
                    <Form>
                        <div className="row g-3">
                            <div className="col-md-6">
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold small text-uppercase">Exam Body Name</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        name="examBody" 
                                        value={editingBody.examBody} 
                                        onChange={handleFormChange}
                                        placeholder="e.g. National Testing Agency"
                                        className="py-2"
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-6">
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold small text-uppercase">Exam Body Value</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        name="examBodyValue" 
                                        value={editingBody.examBodyValue} 
                                        onChange={handleFormChange}
                                        placeholder="e.g. NTA"
                                        className="py-2"
                                    />
                                </Form.Group>
                            </div>
                        </div>

                        <div className="row g-3">
                            <div className="col-md-6">
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold small text-uppercase">Exam Date</Form.Label>
                                    <Form.Control 
                                        type="date" 
                                        name="examDate" 
                                        value={editingBody.examDate} 
                                        onChange={handleFormChange}
                                        className="py-2"
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-6">
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold small text-uppercase">Status</Form.Label>
                                    <Form.Control 
                                        type="text"
                                        name="examStatus" 
                                        value={editingBody.examStatus} 
                                        onChange={handleFormChange}
                                        placeholder="e.g. 0, 1, Active"
                                        className="py-2"
                                    />
                                </Form.Group>
                            </div>

                        </div>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold small text-uppercase">Exam Body URL</Form.Label>
                            <Form.Control 
                                type="text" 
                                name="examBodyUrl" 
                                value={editingBody.examBodyUrl} 
                                onChange={handleFormChange}
                                placeholder="https://example.com"
                                className="py-2"
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer className="bg-light border-top-0 px-4 py-3">
                    <Button variant="link" className="text-muted text-decoration-none" onClick={() => setShowModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleUpdateSubmit} className="px-5 py-2 fw-bold rounded-3 shadow-sm">
                        Update Changes
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default ExamBodyInfo;
